import { actionGameImages, brandLogos, businessInfo, referenceImages, sidebarAssets } from './brandAssets';
import { createDefaultPagesContent, mergePageContent, type EditableItem, type SitePagesContent } from './pageCms';

export type SiteHour = {
  day: string;
  time: string;
};

export type PromoBanner = {
  enabled: boolean;
  text: string;
  linkLabel: string;
  linkHref: string;
};

export type NewsItem = {
  id: string;
  enabled: boolean;
  tag: string;
  title: string;
  excerpt: string;
  date: string;
  publishedAt: string;
  image: string;
  linkLabel: string;
  linkHref: string;
};

export type SiteContent = {
  siteName: string;
  addressLabel: string;
  addressLines: string[];
  phoneDisplay: string;
  phoneHref: string;
  directionsUrl: string;
  mapEmbedUrl: string;
  weeklyHours: SiteHour[];
  promoBanner: PromoBanner;
  news: {
    eyebrow: string;
    title: string;
    subtitle: string;
    items: NewsItem[];
  };
  images: {
    home: {
      hero: string;
      sidebarFeature: string;
    };
    infosPratiques: {
      hero: string;
    };
    actionGame: {
      hero: string;
      intro: string;
      adultCard: string;
      juniorCard: string;
      stats: string;
      gallery: string[];
    };
    sidebar: {
      hero: string;
      lounge: string;
    };
    sidebarFeature: {
      cocktails: string;
      sign: string;
      lounge: string;
      group: string;
    };
    karaoke: {
      hero: string;
    };
    nouvelleActivite: {
      hero: string;
    };
    teamBuilding: {
      hero: string;
      intro: string;
      gallery: string[];
    };
    privatisation: {
      hero: string;
      intro: string;
      gallery: string[];
    };
    seminaire: {
      hero: string;
      intro: string;
      gallery: string[];
    };
    evgEvjf: {
      hero: string;
      feature: string;
      sidebar: string;
      evgImage: string;
      evjfImage: string;
    };
    evjf: {
      hero: string;
      feature: string;
      sidebar: string;
    };
    evg: {
      hero: string;
      feature: string;
      sidebar: string;
    };
    anniversaireEnfant: {
      hero: string;
      feature: string;
      sidebar: string;
    };
    anniversaireAdulte: {
      hero: string;
      feature: string;
      sidebar: string;
    };
  };
  brand: {
    logos: {
      primary: string;
      vertical: string;
      actionGame: string;
      sidebar: string;
      sidebarPictogram: string;
    };
    pictograms: {
      aztecQuest: string;
      magicSchool: string;
      pirateBay: string;
      starsZone: string;
    };
  };
  pages: SitePagesContent;
};

export type AdminImageField = {
  path: string;
  label: string;
  type: 'string' | 'list';
  description?: string;
};

export type AdminImageGroup = {
  title: string;
  fields: AdminImageField[];
};

export const defaultSiteContent: SiteContent = {
  siteName: businessInfo.siteName,
  addressLabel: businessInfo.addressLabel,
  addressLines: [...businessInfo.addressLines],
  phoneDisplay: businessInfo.phoneDisplay,
  phoneHref: businessInfo.phoneHref,
  directionsUrl: businessInfo.directionsUrl,
  mapEmbedUrl: businessInfo.mapEmbedUrl,
  weeklyHours: businessInfo.weeklyHours.map((slot) => ({ ...slot })),
  promoBanner: {
    enabled: true,
    text: 'Ouverture du Gameside à Caen le **8 mai 2026** — [350 Rue Paul Boucherot, 14123 Ifs](https://www.google.com/maps/dir/?api=1&destination=350+Rue+Paul+Boucherot,+14123+Ifs)',
    linkLabel: '',
    linkHref: '',
  },
  news: {
    eyebrow: 'Actualités & événements',
    title: 'Les actualités et événements de Gameside Caen',
    subtitle: "Retrouvez ici toutes les actualités et événements à venir chez Gameside Caen : ouvertures, soirées privées, animations, offres de groupe et lancement des nouvelles activités à Ifs.",
    items: [
      {
        id: 'opening',
        enabled: true,
        tag: 'Ouverture',
        title: 'Ouverture du Gameside à Caen le vendredi 8 mai 2026',
        excerpt: "Rendez-vous le **vendredi 8 mai 2026** pour l'ouverture officielle du complexe à Ifs, à 5 minutes de Caen. Action Game, salles immersives et activités de groupe vous attendent dès le premier jour.",
        date: '2026-05-08',
        publishedAt: '2026-05-08',
        image: '/brand/action-game/home-hero.webp',
        linkLabel: 'Voir les infos pratiques',
        linkHref: '/infos-pratiques',
      },
    ],
  },
  images: {
    home: {
      hero: '/brand/action-game/home-hero.webp',
      sidebarFeature: '/brand/action-game/home-detail.webp',
    },
    infosPratiques: {
      hero: referenceImages.teamBuilding.intro,
    },
    actionGame: {
      hero: '/brand/action-game/bataille-navale-hero.webp',
      intro: '/brand/action-game/home-activity.webp',
      adultCard: '/brand/action-game/demineur-card.webp',
      juniorCard: '/brand/action-game/serpent-card.webp',
      stats: '/brand/action-game/home-detail.webp',
      gallery: [...actionGameImages.gallery],
    },
    sidebar: {
      hero: sidebarAssets.hero,
      lounge: sidebarAssets.lounge,
    },
    sidebarFeature: {
      cocktails: '/brand/sidebar-bar/cocktails.webp',
      sign: '/brand/sidebar-bar/sign.webp',
      lounge: '/brand/sidebar-bar/lounge.webp',
      group: '/brand/sidebar-bar/group.webp',
    },
    karaoke: {
      hero: '/brand/karaoke/karaoke-room-group-unsplash.webp',
    },
    nouvelleActivite: {
      hero: referenceImages.teamBuilding.gallery[0],
    },
    teamBuilding: {
      hero: '/brand/business/team-building-hero.webp',
      intro: '/brand/business/gallery-06.webp',
      gallery: [
        '/brand/business/gallery-06.webp',
        '/brand/business/gallery-05.webp',
        '/brand/business/gallery-08.webp',
        '/brand/business/gallery-02.webp',
        '/brand/business/gallery-10.webp',
        '/brand/business/gallery-07.webp',
      ],
    },
    privatisation: {
      hero: '/brand/business/privatisation-hero.webp',
      intro: '/brand/business/animation-bg.webp',
      gallery: [
        '/brand/business/gallery-02.webp',
        '/brand/business/gallery-03.webp',
        '/brand/business/gallery-07.webp',
        '/brand/business/gallery-08.webp',
        '/brand/business/gallery-09.webp',
        '/brand/business/gallery-05.webp',
      ],
    },
    seminaire: {
      hero: '/brand/business/seminaire-hero.webp',
      intro: '/brand/business/seminaire-intro.webp',
      gallery: [
        '/brand/business/seminaire-intro.webp',
        '/brand/business/gallery-01.webp',
        '/brand/business/gallery-06.webp',
        '/brand/business/gallery-05.webp',
        '/brand/business/gallery-04.webp',
        '/brand/business/gallery-08.webp',
      ],
    },
    evgEvjf: {
      hero: referenceImages.evjf.hero,
      feature: '/brand/reference/evjf-feature.webp',
      sidebar: referenceImages.cards.evjf,
      evgImage: referenceImages.cards.evg,
      evjfImage: referenceImages.cards.evjf,
    },
    evjf: {
      hero: referenceImages.evjf.hero,
      feature: '/brand/reference/evjf-feature.webp',
      sidebar: referenceImages.cards.evjf,
    },
    evg: {
      hero: referenceImages.evg.hero,
      feature: '/brand/reference/evg-feature.webp',
      sidebar: referenceImages.cards.evg,
    },
    anniversaireEnfant: {
      hero: referenceImages.anniversaireEnfant.hero,
      feature: '/brand/reference/anniversaire-enfant-feature.webp',
      sidebar: referenceImages.anniversaireEnfant.sidebar,
    },
    anniversaireAdulte: {
      hero: referenceImages.anniversaireAdulte.hero,
      feature: '/brand/reference/anniversaire-adulte-feature.webp',
      sidebar: referenceImages.cards.anniversaireAdulte,
    },
  },
  brand: {
    logos: {
      primary: brandLogos.primary,
      vertical: brandLogos.vertical,
      actionGame: brandLogos.actionGame,
      sidebar: sidebarAssets.logo,
      sidebarPictogram: sidebarAssets.pictogram,
    },
    pictograms: {
      aztecQuest: '/brand/pictograms/aztec-quest.webp',
      magicSchool: '/brand/pictograms/magic-school.webp',
      pirateBay: '/brand/pictograms/pirate-bay.webp',
      starsZone: '/brand/pictograms/stars-zone.webp',
    },
  },
  pages: createDefaultPagesContent(),
};

export const adminImageGroups: AdminImageGroup[] = [
  {
    title: 'Identité visuelle',
    fields: [
      { path: 'brand.logos.primary', label: 'Logo principal', type: 'string' },
      { path: 'brand.logos.vertical', label: 'Logo vertical', type: 'string' },
      { path: 'brand.logos.actionGame', label: 'Logo Action Game', type: 'string' },
      { path: 'brand.pictograms.aztecQuest', label: 'Pictogramme Aztec Quest', type: 'string' },
      { path: 'brand.pictograms.magicSchool', label: 'Pictogramme Magic School', type: 'string' },
      { path: 'brand.pictograms.pirateBay', label: 'Pictogramme Pirate Bay', type: 'string' },
      { path: 'brand.pictograms.starsZone', label: 'Pictogramme Stars Zone', type: 'string' },
    ],
  },
  {
    title: 'Accueil',
    fields: [
      { path: 'images.home.hero', label: 'Hero accueil', type: 'string' },
      { path: 'images.home.sidebarFeature', label: 'Image accueil secondaire', type: 'string' },
    ],
  },
  {
    title: 'Action Game',
    fields: [
      { path: 'images.actionGame.hero', label: 'Hero Action Game', type: 'string' },
      { path: 'images.actionGame.intro', label: 'Image intro Action Game', type: 'string' },
      { path: 'images.actionGame.adultCard', label: 'Carte tarifs adultes', type: 'string' },
      { path: 'images.actionGame.juniorCard', label: 'Carte tarifs junior', type: 'string' },
      { path: 'images.actionGame.stats', label: 'Bannière stats', type: 'string' },
      { path: 'images.actionGame.gallery', label: 'Galerie Action Game', type: 'list', description: 'Une URL par ligne.' },
    ],
  },
  {
    title: 'Activités à venir',
    fields: [
      { path: 'images.karaoke.hero', label: 'Hero Karaoké', type: 'string' },
      { path: 'images.nouvelleActivite.hero', label: 'Hero Nouvelle activité', type: 'string' },
    ],
  },
  {
    title: 'Entreprises',
    fields: [
      { path: 'images.teamBuilding.hero', label: 'Hero Team Building', type: 'string' },
      { path: 'images.teamBuilding.intro', label: 'Intro Team Building', type: 'string' },
      { path: 'images.teamBuilding.gallery', label: 'Galerie Team Building', type: 'list', description: 'Une URL par ligne.' },
      { path: 'images.privatisation.hero', label: 'Hero Privatisation', type: 'string' },
      { path: 'images.privatisation.intro', label: 'Intro Privatisation', type: 'string' },
      { path: 'images.privatisation.gallery', label: 'Galerie Privatisation', type: 'list', description: 'Une URL par ligne.' },
      { path: 'images.seminaire.hero', label: 'Hero Séminaire', type: 'string' },
      { path: 'images.seminaire.intro', label: 'Intro Séminaire', type: 'string' },
      { path: 'images.seminaire.gallery', label: 'Galerie Séminaire', type: 'list', description: 'Une URL par ligne.' },
    ],
  },
  {
    title: 'Formules',
    fields: [
      { path: 'images.evgEvjf.hero', label: 'Hero EVG / EVJF', type: 'string' },
      { path: 'images.evgEvjf.feature', label: 'Image contenu EVG / EVJF', type: 'string' },
      { path: 'images.evgEvjf.sidebar', label: 'Carte EVG / EVJF accueil / menu', type: 'string' },
      { path: 'images.evgEvjf.evgImage', label: 'Image EVG gauche (diagonale)', type: 'string' },
      { path: 'images.evgEvjf.evjfImage', label: 'Image EVJF droite (diagonale)', type: 'string' },
      { path: 'images.anniversaireEnfant.hero', label: 'Hero anniversaire enfant', type: 'string' },
      { path: 'images.anniversaireEnfant.feature', label: 'Image contenu anniversaire enfant', type: 'string' },
      { path: 'images.anniversaireEnfant.sidebar', label: 'Carte anniversaire enfant accueil / menu', type: 'string' },
    ],
  },
  {
    title: 'Infos pratiques',
    fields: [
      { path: 'images.infosPratiques.hero', label: 'Hero infos pratiques', type: 'string' },
    ],
  },
];

export function cloneSiteContent(content: SiteContent = defaultSiteContent): SiteContent {
  return JSON.parse(JSON.stringify(content)) as SiteContent;
}

function removeSidebarContent(pages: SitePagesContent) {
  delete pages.sidebarBar;

  // Saved CMS snapshots can still contain retired Sidebar questions, even
  // after they have been removed from the default content and shared section.
  for (const page of Object.values(pages)) {
    if (Array.isArray(page.faq?.items)) {
      page.faq.items = (page.faq.items as EditableItem[]).filter((item) => (
        !/\bside[\s-]*bar\b/i.test(`${item.question || ''} ${item.answer || ''}`)
      ));
    }
  }
}

function normalizePublicFormulaContent(pages: SitePagesContent, defaultPages: SitePagesContent) {
  delete pages.evjf;
  delete pages.evg;
  delete pages.anniversaireAdulte;

  const defaultCards = defaultPages.home?.formulas?.cards;
  const defaultFormulaCards = Array.isArray(defaultCards) ? defaultCards as EditableItem[] : [];
  const combinedDefault = defaultFormulaCards.find((card) => card.link === '/formules/evg-evjf');
  const childDefault = defaultFormulaCards.find((card) => card.link === '/formules/anniversaire-enfant');
  const currentCards = pages.home?.formulas?.cards;

  if (pages.home?.formulas && Array.isArray(currentCards)) {
    const childCard = (currentCards as EditableItem[]).find((card) => {
      const title = String(card.title || '').toLowerCase();
      return card.link === '/formules/anniversaire-enfant' || title.includes('anniversaire enfant');
    });

    pages.home.formulas.cards = [
      combinedDefault,
      childCard || childDefault,
    ].filter(Boolean) as EditableItem[];
  }

  const infoFaqItems = pages.infosPratiques?.faq?.items;
  if (Array.isArray(infoFaqItems)) {
    pages.infosPratiques.faq.items = (infoFaqItems as EditableItem[]).map((item) => {
      if (String(item.question || '').toLowerCase().includes('evg') && String(item.question || '').toLowerCase().includes('evjf')) {
        return {
          ...item,
          answer: "Très souvent demandé. La formule EVG / EVJF combine l'activité et un temps plus convivial sur place, avec une seule page dédiée dans le menu Formules.",
        };
      }

      return item;
    });
  }
}

export function mergeSiteContent(input?: Partial<SiteContent> | null): SiteContent {
  const base = cloneSiteContent(defaultSiteContent);
  if (!input) {
    return base;
  }

  const source = input as Partial<SiteContent>;

  const merged: SiteContent = {
    ...base,
    ...source,
    addressLines: Array.isArray(source.addressLines) && source.addressLines.length > 0 ? source.addressLines.filter(Boolean) : base.addressLines,
    weeklyHours: Array.isArray(source.weeklyHours) && source.weeklyHours.length > 0
      ? source.weeklyHours.map((slot, index) => ({
          day: slot?.day || base.weeklyHours[index]?.day || `Jour ${index + 1}`,
          time: slot?.time || '',
        }))
      : base.weeklyHours,
    promoBanner: {
      ...base.promoBanner,
      ...(source.promoBanner || {}),
      enabled: typeof source.promoBanner?.enabled === 'boolean' ? source.promoBanner.enabled : base.promoBanner.enabled,
    },
    news: {
      ...base.news,
      ...(source.news || {}),
      items: Array.isArray(source.news?.items) && source.news.items.length > 0
        ? source.news.items.map((item, index) => {
            const itemBase = base.news.items[index] || {
              id: `news-${index + 1}`,
              enabled: false,
              tag: '',
              title: '',
              excerpt: '',
              date: '',
              publishedAt: '',
              image: '',
              linkLabel: '',
              linkHref: '',
            };

            return {
              ...itemBase,
              ...(item || {}),
              id: item?.id || itemBase.id,
              enabled: typeof item?.enabled === 'boolean' ? item.enabled : itemBase.enabled,
              publishedAt: item?.publishedAt || item?.date || itemBase.publishedAt || itemBase.date || '',
            };
          })
        : base.news.items,
    },
    images: {
      ...base.images,
      ...(source.images || {}),
      home: { ...base.images.home, ...(source.images?.home || {}) },
      infosPratiques: { ...base.images.infosPratiques, ...(source.images?.infosPratiques || {}) },
      actionGame: {
        ...base.images.actionGame,
        ...(source.images?.actionGame || {}),
        gallery: Array.isArray(source.images?.actionGame?.gallery) && source.images.actionGame.gallery.length > 0
          ? source.images.actionGame.gallery.filter(Boolean)
          : base.images.actionGame.gallery,
      },
      sidebar: { ...base.images.sidebar, ...(source.images?.sidebar || {}) },
      sidebarFeature: { ...base.images.sidebarFeature, ...(source.images?.sidebarFeature || {}) },
      karaoke: { ...base.images.karaoke, ...(source.images?.karaoke || {}) },
      nouvelleActivite: { ...base.images.nouvelleActivite, ...(source.images?.nouvelleActivite || {}) },
      teamBuilding: {
        ...base.images.teamBuilding,
        ...(source.images?.teamBuilding || {}),
        gallery: Array.isArray(source.images?.teamBuilding?.gallery) && source.images.teamBuilding.gallery.length > 0
          ? source.images.teamBuilding.gallery.filter(Boolean)
          : base.images.teamBuilding.gallery,
      },
      privatisation: {
        ...base.images.privatisation,
        ...(source.images?.privatisation || {}),
        gallery: Array.isArray(source.images?.privatisation?.gallery) && source.images.privatisation.gallery.length > 0
          ? source.images.privatisation.gallery.filter(Boolean)
          : base.images.privatisation.gallery,
      },
      seminaire: {
        ...base.images.seminaire,
        ...(source.images?.seminaire || {}),
        gallery: Array.isArray(source.images?.seminaire?.gallery) && source.images.seminaire.gallery.length > 0
          ? source.images.seminaire.gallery.filter(Boolean)
          : base.images.seminaire.gallery,
      },
      evgEvjf: { ...base.images.evgEvjf, ...(source.images?.evgEvjf || {}) },
      evjf: { ...base.images.evjf, ...(source.images?.evjf || {}) },
      evg: { ...base.images.evg, ...(source.images?.evg || {}) },
      anniversaireEnfant: { ...base.images.anniversaireEnfant, ...(source.images?.anniversaireEnfant || {}) },
      anniversaireAdulte: { ...base.images.anniversaireAdulte, ...(source.images?.anniversaireAdulte || {}) },
    },
    brand: {
      ...base.brand,
      ...(source.brand || {}),
      logos: { ...base.brand.logos, ...(source.brand?.logos || {}) },
      pictograms: { ...base.brand.pictograms, ...(source.brand?.pictograms || {}) },
    },
    pages: base.pages,
  };

  const defaultPages = createDefaultPagesContent(merged.images, merged.brand);
  merged.pages = mergePageContent(defaultPages, source.pages);
  removeSidebarContent(merged.pages);

  // Older CMS snapshots still contain the pre-launch page that was hidden by
  // the hard-coded booking page. Only replace that known legacy starter copy.
  if (merged.pages.karaoke.hero.title === 'KARAOKÉ CAEN\nBIENTÔT À IFS'
    && typeof source.pages?.karaoke?.hero?.description !== 'string') {
    merged.pages.karaoke.hero = { ...defaultSiteContent.pages.karaoke.hero };
  }

  normalizePublicFormulaContent(merged.pages, defaultPages);

  // Keep compliance-sensitive analytics disclosures aligned with the tracking
  // implementation, even when an older CMS snapshot is injected at build time.
  if (merged.pages.legal && defaultPages.legal) {
    const defaultCookieParagraphs = defaultPages.legal.privacy.cookiesParagraphs;
    if (Array.isArray(defaultCookieParagraphs)) {
      merged.pages.legal.privacy.cookiesParagraphs = defaultCookieParagraphs.map(String);
    }
    merged.pages.legal.cookies.generalText = defaultPages.legal.cookies.generalText;
    merged.pages.legal.cookies.refusedText = defaultPages.legal.cookies.refusedText;
  }

  return merged;
}
